// ═══════════════════════════════════════════════════════════════════
//  modules/ah/AhJournal.js
//  AhJournal
// ═══════════════════════════════════════════════════════════════════

import { useCallback, useEffect, useState } from "react";
import { API_BASE } from "../../socle/api";
import { THEME } from "../../socle/theme";
import { actionInfo, argent, erreurDe, etatInfo, horodate, nomObjet, nombre } from "./ah";
import { MessageErreur } from "./AhBoites";

const VIDE = { action: "", joueur: "", serveur: "", depuis: "", jusqua: "" };

export function AhJournal({ token, onOuvrirAnnonce }) {
  const [filtres, setFiltres] = useState(VIDE);
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = new URLSearchParams();
      Object.entries(filtres).forEach(([k, v]) => { if (v) q.set(k, v); });
      q.set("page", String(page));
      const res = await fetch(`${API_BASE}/api/ah/journal?${q}`, { headers: { Authorization: `Bearer ${token}` } });
      setData(await res.json());
    } catch (err) { console.error(err); }
    setLoading(false);
  }, [token, filtres, page]);
  useEffect(() => { load(); }, [load]);

  // Changer un filtre ramène en page 1 : rester en page 4 d'un résultat
  // qui n'en a plus que 2 affiche un tableau vide, et on croit que le
  // filtre n'a rien trouvé.
  const changer = (k, v) => { setPage(1); setFiltres((f) => ({ ...f, [k]: v })); };
  const actif = Object.values(filtres).some(Boolean);
  const erreur = loading && !data ? null : erreurDe(data);

  const champ = {
    background: THEME.surface, border: `1px solid ${THEME.border}`,
    color: THEME.text, borderRadius: 6, padding: "7px 10px", fontSize: 12,
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>

      {/* ── Les filtres ───────────────────────────────────────────── */}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
        <select style={{ ...champ, minWidth: 150 }} value={filtres.action} onChange={(e) => changer("action", e.target.value)}>
          <option value="">Toutes les actions</option>
          {/* Les valeurs viennent de la base, pas d'une liste écrite ici : */}
          {/* un libellé ajouté par le plugin apparaît sans rien toucher.   */}
          {(data?.actions || []).map((a) => (
            <option key={a.action} value={a.action}>{actionInfo(a.action).label} ({a.total})</option>
          ))}
        </select>

        <input style={{ ...champ, minWidth: 180 }} placeholder="Pseudo ou UUID"
          value={filtres.joueur} onChange={(e) => changer("joueur", e.target.value)} />

        <select style={champ} value={filtres.serveur} onChange={(e) => changer("serveur", e.target.value)}>
          <option value="">Les deux serveurs</option>
          {(data?.serveurs || []).map((s) => <option key={s} value={s}>{s}</option>)}
        </select>

        <input style={champ} type="date" value={filtres.depuis} onChange={(e) => changer("depuis", e.target.value)} title="À partir du" />
        <input style={champ} type="date" value={filtres.jusqua} onChange={(e) => changer("jusqua", e.target.value)} title="Jusqu'au inclus" />

        {actif && (
          <button onClick={() => { setPage(1); setFiltres(VIDE); }}
            style={{ ...champ, cursor: "pointer", color: THEME.textMuted }}>Effacer</button>
        )}
        <span style={{ marginLeft: "auto", color: THEME.textMuted, fontSize: 12 }}>
          {loading ? "…" : `${nombre(data?.total || 0)} ligne${(data?.total || 0) > 1 ? "s" : ""}`}
        </span>
      </div>

      {/* ── Le tableau ────────────────────────────────────────────── */}
      {loading && !data ? (
        <div style={{ color: THEME.textMuted }}>Chargement du journal...</div>
      ) : erreur ? (
        <MessageErreur texte={erreur} />
      ) : data.lignes.length === 0 ? (
        <div style={{ color: THEME.textMuted, background: THEME.surface, borderRadius: 8, padding: "20px", textAlign: "center", fontSize: 13 }}>
          {actif ? "Aucune ligne ne correspond à ces filtres." : "Le journal est vide."}
        </div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, minWidth: 760 }}>
            <thead>
              <tr style={{ color: THEME.textMuted, textAlign: "left" }}>
                {["Quand", "Action", "Qui", "Sur quoi", "Montant", "Serveur"].map((h) => (
                  <th key={h} style={{ padding: "7px 10px", fontWeight: 600, fontSize: 10, textTransform: "uppercase", borderBottom: `1px solid ${THEME.border}`, whiteSpace: "nowrap" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.lignes.map((l) => {
                const a = actionInfo(l.action);
                return (
                  <tr key={l.id} style={{ borderBottom: `1px solid ${THEME.border}` }}>
                    <td style={{ padding: "7px 10px", color: THEME.textMuted, whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>{horodate(l.horodatage)}</td>
                    <td style={{ padding: "7px 10px", whiteSpace: "nowrap" }}>
                      <span style={{ color: a.color, fontWeight: 600 }}>{a.emoji} {a.label}</span>
                      {a.inconnue && <span style={{ color: THEME.amber, fontSize: 9, marginLeft: 6 }} title="Libellé écrit par le plugin et inconnu de l'intranet">nouveau</span>}
                    </td>
                    <td style={{ padding: "7px 10px", color: THEME.text, whiteSpace: "nowrap" }}>{l.acteur_pseudo || "—"}</td>
                    <td style={{ padding: "7px 10px", color: THEME.text }}>
                      <SurQuoi ligne={l} onOuvrirAnnonce={onOuvrirAnnonce} />
                    </td>
                    <td style={{ padding: "7px 10px", color: l.montant ? THEME.emerald : THEME.textDim, whiteSpace: "nowrap", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                      {l.montant === null ? "—" : argent(l.montant)}
                    </td>
                    <td style={{ padding: "7px 10px", color: THEME.textMuted }}>{l.serveur}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* ── La pagination ─────────────────────────────────────────── */}
      {data && data.pages > 1 && (
        <div style={{ display: "flex", gap: 8, alignItems: "center", justifyContent: "center" }}>
          <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)}
            style={{ ...champ, cursor: page <= 1 ? "not-allowed" : "pointer", opacity: page <= 1 ? 0.4 : 1 }}>← Précédent</button>
          <span style={{ color: THEME.textMuted, fontSize: 12 }}>page {data.page} sur {data.pages}</span>
          <button disabled={page >= data.pages} onClick={() => setPage((p) => p + 1)}
            style={{ ...champ, cursor: page >= data.pages ? "not-allowed" : "pointer", opacity: page >= data.pages ? 0.4 : 1 }}>Suivant →</button>
        </div>
      )}
    </div>
  );
}

// ── La colonne « Sur quoi » ────────────────────────────────────────────
//  Trois cas, et il faut que la différence se VOIE :
//   - le journal porte l'annonce      -> on l'affiche, elle est sûre
//   - le journal ne la porte pas mais
//     on l'a retrouvée par l'heure    -> on l'affiche AVEC la réserve
//   - on n'a rien                     -> on le dit, on n'invente pas
function SurQuoi({ ligne, onOuvrirAnnonce }) {
  const lien = (id, texte) => (
    <button onClick={() => onOuvrirAnnonce(id)} title={`Ouvrir l'annonce ${id}`}
      style={{ background: "none", border: "none", padding: 0, color: THEME.accent, cursor: "pointer", fontSize: 12, textAlign: "left", textDecoration: "underline dotted" }}>
      {texte}
    </button>
  );

  if (ligne.annonce) {
    const e = etatInfo(ligne.annonce.etat);
    return (
      <span>
        {lien(ligne.annonce_id, nomObjet(ligne.annonce.identifiant_lisible))}
        <span style={{ color: THEME.textDim }}> · {nombre(ligne.annonce.quantite_initiale)} à {argent(ligne.annonce.prix_unitaire)}</span>
        <span style={{ color: e.color, fontSize: 10, marginLeft: 6 }}>{e.label}</span>
      </span>
    );
  }

  if (ligne.reconstruit && ligne.detail) {
    return (
      <span>
        {ligne.detail.map((d, i) => (
          <span key={i} style={{ display: "block" }}>
            {/* Un dépôt en boîte n'a pas d'annonce à ouvrir ni de prix : */}
            {/* on montre ce qu'on a, et rien de plus.                     */}
            {d.annonce_id
              ? lien(d.annonce_id, nomObjet(d.identifiant_lisible))
              : <span style={{ color: THEME.text }}>{nomObjet(d.identifiant_lisible)}</span>}
            <span style={{ color: THEME.textDim }}>
              {" · "}{nombre(d.quantite)}
              {d.prix_unitaire !== null && <> à {argent(d.prix_unitaire)}</>}
              {d.vendeur_pseudo ? <> chez {d.vendeur_pseudo}</> : <> en boîte</>}
            </span>
          </span>
        ))}
        <span style={{ color: THEME.amber, fontSize: 9 }}
          title="Le plugin n'écrit pas l'annonce sur cette ligne du journal. Retrouvée par le joueur et l'horodatage, dans oa_vente ou oa_boite.">
          ⚠ rapproché, pas écrit dans le journal
        </span>
      </span>
    );
  }

  return <span style={{ color: THEME.textDim }}>—</span>;
}
